import {
  Building2,
  Home,
  LucideIcon,
  MapPinned,
  ShoppingCart,
  UsersIcon,
  Eye,
  TrendingUp,
  ChevronLeft,
  LayoutDashboard,
  Settings,
  Activity,
  Radio,
} from 'lucide-react';
import { useHasPermission, useIsAdmin, useUserManagement } from './useAccess';
import { useLocation, matchPath } from 'react-router-dom';
import { useIsCustomerUser, usePortalAccess } from '@/providers/userProvider';
import { releasedOnly } from '@/utils/environment';

export interface SidebarLink {
  title: string;
  url: string;
  icon: LucideIcon;
  locked?: boolean;
  /** Pre-release feature name (PRE_RELEASE): the link shows only where it's released. */
  feature?: string;
}

export interface SidebarLinks {
  navMain: SidebarLink[];
}

const getCustomerPortalLinks = (isSubscriber: boolean): SidebarLinks => ({
  navMain: [
    { title: 'Dashboard', url: '/home', icon: Home, locked: false },
    { title: 'Live View', url: '/live-view', icon: Eye, locked: !isSubscriber },
    { title: 'Trends', url: '/trends', icon: TrendingUp, locked: !isSubscriber },
    { title: 'My Fleet', url: '/my-fleet', icon: ShoppingCart, locked: !isSubscriber },
  ],
});

const getInternalTopLinks = (
  canSeeCustomers: boolean,
  canSeeUsers: boolean,
  isAdmin: boolean
): SidebarLinks => {
  const links: SidebarLink[] = [
    { title: 'Home', url: '/home', icon: Home },
  ];
  if (canSeeCustomers || isAdmin) {
    links.push({ title: 'Customers', url: '/customers', icon: Building2 });
  }
  if (canSeeUsers || isAdmin) {
    links.push({ title: 'Users', url: '/users', icon: UsersIcon });
  }
  if (isAdmin) {
    links.push({ title: 'Engagement', url: '/engagement', icon: Activity });
    links.push({ title: 'Cart Signals', url: '/cart-signals', icon: Radio, feature: 'Cart Signals' });
  }
  return { navMain: links };
};

const getCustomerContextLinks = (customerId: string): SidebarLinks => ({
  navMain: [
    { title: 'Back to Customers', url: '/customers', icon: ChevronLeft },
    { title: 'Dashboard', url: `/customers/${customerId}`, icon: LayoutDashboard },
    { title: 'Locations', url: `/customers/${customerId}/locations`, icon: MapPinned },
    { title: 'Users', url: `/customers/${customerId}/users`, icon: UsersIcon },
    { title: 'Settings', url: `/customers/${customerId}/settings`, icon: Settings },
  ],
});

const isInCustomerContext = (pathname: string): string | null => {
  // Match /customers/:customerId or any deeper, but NOT /customers/manage and NOT bare /customers
  const match = matchPath('/customers/:customerId/*', pathname) ||
    matchPath('/customers/:customerId', pathname);
  if (!match) return null;
  const cid = match.params.customerId;
  if (!cid || cid === 'manage') return null;
  return cid;
};

export const useSidebarLinks = (): SidebarLinks => {
  const location = useLocation();
  const isAdmin = useIsAdmin();
  const isCustomerUser = useIsCustomerUser();
  const { isSubscriber } = usePortalAccess();
  // Service users add and manage customer portal users there (api#304).
  const { isService } = useUserManagement();
  const canSeeUsers = (useHasPermission('api.view_user').hasPermission ?? false) || isService;
  const canSeeCustomers = useHasPermission('api.view_customer').hasPermission ?? false;

  const customerId = isCustomerUser ? null : isInCustomerContext(location.pathname);
  const links = isCustomerUser
    ? getCustomerPortalLinks(isSubscriber)
    : customerId
      ? getCustomerContextLinks(customerId)
      : getInternalTopLinks(canSeeCustomers, canSeeUsers, isAdmin);
  return { navMain: releasedOnly(links.navMain) };
};
